'use client'

import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { LogOut, ChevronDown } from 'lucide-react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Cards01Icon,
  Comment02Icon,
  Calendar04Icon,
  UserIcon,
  GameController01Icon,
} from '@hugeicons/core-free-icons'
import Link from "next/link"
import posthog from 'posthog-js'
import { usePathname } from "next/navigation"
import { GameDropdown } from "@/components/GameDropdown"
import { UserAvatar } from "@/components/ui/user-avatar"
import { useI18n } from "@/components/I18nProvider"
import { useAuth } from '@/contexts/AuthContext'
import { LocalClock } from '@/components/LocalClock'
import { MobileMenuSheet, MobileMenuToggle } from '@/components/MobileNavMenu'
import { GetAppButton } from '@/components/GetAppButton'
import { NAV_HEIGHT } from '@/lib/nav-height'
import { BrandLogo } from '@/components/BrandLogo'

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const [menuTop, setMenuTop] = useState(0)
  const pathname = usePathname()
  // The clock is a reference for the localized event times, so it only earns
  // its place in the header on the tournament routes that show them.
  const isTournamentsRoute = /^\/[a-z]{2}\/tournaments(\/|$)/.test(pathname ?? '')
  const { session, user, signOut } = useAuth()
  const context = useI18n()
  const t = context?.t || {}

  const handleSignOut = async () => {
    await signOut()
    setUserMenuOpen(false)
    // Redirect to home page
    window.location.href = `/${locale}`
  }

  const locale = useMemo(() => {
    const seg = pathname?.split("/")[1]
    return seg === "es" ? "es" : seg === "ja" ? "ja" : "en"
  }, [pathname])

  const hrefWithLocale = (path: string) => `/${locale}${path === '/' ? '' : (path.startsWith('/') ? path : `/${path}`)}`

  // In the signed-in user dropdown (the private app menu), Tournaments points at
  // the in-app dashboard; the main nav link below always goes to the public list.
  const tournamentsPath = session ? '/app/tournaments' : '/tournaments'

  // Mirror of the in-app AppHeader nav so logged-in users get the full menu here too.
  const appNavLinks = [
    { path: '/app/explore', label: t.nav?.explore || 'Explore', icon: Cards01Icon },
    { path: '/app/matches', label: t.nav?.chat || 'Chat', icon: Comment02Icon },
    { path: '/app/lfg', label: t.nav?.squads || 'Squads', icon: GameController01Icon },
    { path: tournamentsPath, label: t.nav?.tournaments || 'Tournaments', icon: Calendar04Icon },
    { path: '/app', label: t.nav?.myProfile || 'My Profile', icon: UserIcon },
  ]

  const switchLocaleHref = useMemo(() => {
    const segs = pathname?.split('/') || []
    const nextLocale = locale === 'en' ? 'es' : locale === 'es' ? 'ja' : 'en'
    if (segs.length > 1) {
      segs[1] = nextLocale
    }
    const nextPath = segs.join('/') || '/'
    return nextPath.startsWith('/') ? nextPath : `/${nextPath}`
  }, [pathname, locale])

  const localeHref = (target: 'en' | 'es' | 'ja') => {
    const segs = (pathname?.split('/') || []);
    if (segs.length > 1) segs[1] = target;
    const nextPath = segs.join('/') || '/';
    return nextPath.startsWith('/') ? nextPath : `/${nextPath}`;
  };

  useEffect(() => {
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close user menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  const openMobileMenu = useCallback(() => {
    setMenuTop(navRef.current?.getBoundingClientRect().bottom ?? 0)
    setIsMobileMenuOpen(true)
    posthog.capture('mobile_nav_opened', { platform: 'web' })
  }, [])

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
    toggleRef.current?.focus({ preventScroll: true })
  }, [])

  // Close the mobile menu on navigation (back/forward included).
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [pathname])

  // While the mobile menu is open: lock page scroll, move focus into the menu,
  // and close it if the viewport grows past the mobile breakpoint.
  useEffect(() => {
    if (!isMobileMenuOpen) return
    const html = document.documentElement
    const prevHtml = html.style.overflow
    const prevBody = document.body.style.overflow
    html.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'

    const focusTimer = window.setTimeout(() => {
      document
        .querySelector<HTMLElement>('#mobile-nav-menu a[href], #mobile-nav-menu button')
        ?.focus({ preventScroll: true })
    }, 60)

    const mq = window.matchMedia('(min-width: 768px)')
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setIsMobileMenuOpen(false)
    }
    mq.addEventListener('change', onChange)

    return () => {
      html.style.overflow = prevHtml
      document.body.style.overflow = prevBody
      window.clearTimeout(focusTimer)
      mq.removeEventListener('change', onChange)
    }
  }, [isMobileMenuOpen])

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 w-full ${NAV_HEIGHT} transition-colors duration-300 border-b ${isMobileMenuOpen ? 'z-[70] max-md:bg-background max-md:border-white/10' : 'z-50'} ${isScrolled ? 'bg-background/95 md:backdrop-blur-md border-border' : 'bg-transparent border-transparent'}`}
    >
      <div className="container relative z-20 mx-auto flex h-full items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <BrandLogo href={hrefWithLocale("/")} />
          {isTournamentsRoute && (
            <LocalClock className="hidden sm:inline text-sm text-muted-foreground tabular-nums" />
          )}
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <GameDropdown />
          <Link
            href={hrefWithLocale("/tournaments")}
            className="relative text-muted-foreground hover:text-foreground transition-colors text-sm font-medium tracking-wide uppercase"
          >
            {t.nav?.tournaments || 'Tournaments'}
            <span className="absolute -top-1 -right-2.5 flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
          </Link>
          <Link
            href={hrefWithLocale("/leaderboard")}
            className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium tracking-wide uppercase"
          >
            {t.nav?.leaderboard || 'Leaderboard'}
          </Link>

          {/* Get app + Auth Buttons, grouped tight like the user pill they sit next to */}
          <div className="flex items-center gap-3">
          <GetAppButton surface="landing_nav" />
          {session ? (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] py-1 pl-1 pr-2.5 transition-colors hover:bg-white/10 cursor-pointer"
              >
                <UserAvatar
                  url={user?.profile_image_url}
                  alt={user?.gamertag ?? 'You'}
                  size={28}
                  className="ring-1 ring-white/10"
                />
                <span className="text-sm font-medium truncate max-w-[120px]">
                  {user?.gamertag ?? session.user.email?.split('@')[0]}
                </span>
                <ChevronDown
                  size={15}
                  className={`text-white/50 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-52 rounded-xl bg-card border border-white/10 shadow-xl overflow-hidden z-50">
                  {appNavLinks.map(({ path, label, icon: Icon }) => (
                    <Link
                      key={path}
                      href={hrefWithLocale(path)}
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <HugeiconsIcon icon={Icon} size={16} strokeWidth={2} />
                      {label}
                    </Link>
                  ))}
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-white/80 hover:bg-white/5 hover:text-white transition-colors border-t border-white/10"
                  >
                    <LogOut size={16} />
                    {t.nav?.signOut || 'Sign Out'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              href={hrefWithLocale("/login")}
              className="py-2 px-4 rounded-lg bg-primary hover:bg-primary/90 text-sm font-bold uppercase tracking-wide transition-colors"
            >
              {t.nav?.signIn || 'Sign In'}
            </Link>
          )}
          </div>
        </div>

        {/* Mobile Toggle */}
        <MobileMenuToggle
          open={isMobileMenuOpen}
          onToggle={() => (isMobileMenuOpen ? closeMobileMenu() : openMobileMenu())}
          buttonRef={toggleRef}
        />
      </div>

      {/* Mobile Menu */}
      <MobileMenuSheet
        open={isMobileMenuOpen}
        onClose={closeMobileMenu}
        top={menuTop}
        pathname={pathname ?? ''}
        hrefWithLocale={hrefWithLocale}
        t={t}
        session={session}
        user={user}
        fallbackName={session?.user?.email?.split('@')[0]}
        appLinks={appNavLinks}
        onSignOut={handleSignOut}
        trapRef={navRef}
      />
    </nav>
  )
}
